import { hexLen, hexToWorld, type Hex } from '../hex.ts';
import { isqrt } from '../math/fixed.ts';
import { deriveSeed, rngInt, rngRange, seedRng, type RngState } from '../math/rng.ts';

/**
 * The ranger's path, chapter 2 (docs/sendas/explorador.md, «El Acantilado del Flanco»): its grounds from the seed, the
 * same in the sim and the drawing, integers only. Up the wall is −y (the top of the screen), the sea at the foot (+y).
 *
 * - `edgeLayout` (act 1, the cornice): one wide ledge at the top of the wall, the rock rising north of it with the
 *   crows' nests and the deep cracks of the shriekers, the drop to the sea south; Sela's anchor at its south lip.
 * - `wallLayout` (acts 2 and 3): the wall seen from the front. Ledges at four levels five rows apart (two rows deep
 *   each), split in segments; the faces between them with cracks (firm, hollow, crumbling). A firm crack holds a rope
 *   down to the ledge under it; a crumbling one hangs over the void and holds who swings to it a while; a hollow one
 *   drops the arrow. The rope reaches `ROPE`: from a ledge only the face just under it; from a crumbling crack, the
 *   next face down too (so some ledges are only reached from a crumbling crack).
 *
 * Checked on paper (cliffRoutes, cliffObvious; test with many seeds): from the top there are one or two ways down to
 * the goal using firm and crumbling cracks. The wall (act 2) has a hollow crack in reach of the top and a way through a
 * crumbling crack. The night (act 3, the test): the last ledge is out of reach from every ledge (only a crumbling crack
 * gets there), and the obvious, down the nearest firm crack each time, always ends on a ledge with no way on.
 */

export const EDGE_RADIUS = 10;
export const WALL_RADIUS = 11;
/** The rope's reach (the ranger's base range: docs/sendas/explorador.md §4, «Las anclas»). */
export const ROPE = 6000;
/** The rows of the levels' ledges (the top row of each; a ledge is two rows deep). */
export const LEVEL_ROWS = [-10, -5, 0, 5];

export interface CliffCrackLayout {
  h: Hex;
  /** 0 firm, 1 hollow, 2 crumbling. */
  kind: number;
  /** The ledge under it (−1: the void), and the one over it (−1: none: a rope there only goes down). */
  land: number;
  top: number;
}

export interface CliffLedgeLayout {
  cells: Hex[];
  level: number;
  /** Its x span (world, thousandths of a hex). */
  x0: number;
  x1: number;
  goal: boolean;
}

export interface CliffLayout {
  rocks: Hex[];
  ledges: CliffLedgeLayout[];
  cracks: CliffCrackLayout[];
  nests: Hex[];
  holes: Hex[];
  stones: { h: Hex; ledge: number }[];
  start: Hex;
  /** Act 1: Sela's anchor. */
  anchor: Hex;
  /** The side the crew goes down (−1 west, +1 east). */
  crew: number;
}

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};
const key = (h: Hex): string => `${h.q},${h.r}`;
const wx = (h: Hex): number => hexToWorld(h.q, h.r).x;
/** The hex of row `r` nearest world x `x`. */
const atRow = (r: number, x: number): Hex => {
  const a = x - 500 * r;
  return { q: Math.trunc((a + (a >= 0 ? 500 : -500)) / 1000), r };
};
const d2 = (a: { x: number; y: number }, b: { x: number; y: number }): number => (a.x - b.x) * (a.x - b.x) + (a.y - b.y) * (a.y - b.y);
/**
 * In reach on paper: a margin short of the rope (the fight shoots from a cell a little short of it, and a hero hanging
 * hangs a little under its crack): what is a way on paper is always one in the fight.
 */
const PAPER = ROPE - 300;
const inReach = (a: { x: number; y: number }, b: { x: number; y: number }): boolean => d2(a, b) <= PAPER * PAPER;

/** The cells of a ledge: rows `row` and `row + 1`, x within its span. */
const ledgeCells = (R: number, row: number, x0: number, x1: number): Hex[] => {
  const out: Hex[] = [];
  for (const r of [row, row + 1]) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R - 1 && wx({ q, r }) >= x0 && wx({ q, r }) <= x1) out.push({ q, r });
  return out;
};

const rocksOf = (R: number, open: Hex[]): Hex[] => {
  const o = new Set(open.map(key));
  return allCells(R + 2).filter((h) => !o.has(key(h)));
};

/* ---------- act 1: the cornice ---------- */

export const edgeLayout = (seed: number): CliffLayout => {
  const R = EDGE_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xed9e));
  const ledge: CliffLedgeLayout = { cells: [], level: 0, x0: -7500, x1: 7500, goal: false };
  for (let r = -2; r <= 2; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R - 1 && Math.abs(wx({ q, r })) <= 7500) ledge.cells.push({ q, r });
  const nests: Hex[] = [];
  const holes: Hex[] = [];
  for (let i = 0; i < 5; i++) nests.push(atRow(-3 - (i % 2), -6000 + i * 3000 + rngRange(rng, -600, 600)));
  for (let i = 0; i < 3; i++) holes.push(atRow(-3, -4500 + i * 4500 + rngRange(rng, -700, 700)));
  return { rocks: rocksOf(R, ledge.cells), ledges: [ledge], cracks: [], nests, holes, stones: [], start: atRow(0, -1500), anchor: atRow(2, 0), crew: rngInt(rng, 2) ? 1 : -1 };
};

/* ---------- acts 2 and 3: the wall ---------- */

const crackPoint = (c: CliffCrackLayout): { x: number; y: number } => hexToWorld(c.h.q, c.h.r);

/** The nodes of the way down: the ledges (0…n−1), then the crumbling cracks hung from (n + their index). */
const reachFrom = (L: CliffLayout, node: number): { x: number; y: number }[] => {
  const n = L.ledges.length;
  if (node < n) return (L.ledges[node] as CliffLedgeLayout).cells.map((h) => hexToWorld(h.q, h.r));
  return [crackPoint(L.cracks[node - n] as CliffCrackLayout)];
};

/** Where a node goes by each crack in reach: a firm one to the ledge under it, a crumbling one to hanging from it. */
const edgeMemo = new WeakMap<CliffLayout, Map<number, { crack: number; to: number }[]>>();
export const cliffEdges = (L: CliffLayout, node: number): { crack: number; to: number }[] => {
  let m = edgeMemo.get(L);
  if (!m) edgeMemo.set(L, (m = new Map()));
  const got = m.get(node);
  if (got) return got;
  const out = edgesOf(L, node);
  m.set(node, out);
  return out;
};
const edgesOf = (L: CliffLayout, node: number): { crack: number; to: number }[] => {
  const n = L.ledges.length;
  const from = reachFrom(L, node);
  const out: { crack: number; to: number }[] = [];
  L.cracks.forEach((c, i) => {
    if (c.kind === 1 || node === n + i) return;
    const p = crackPoint(c);
    if (!from.some((f) => inReach(f, p))) return;
    // A firm crack joins the ledge over it and the one under it: shot from under, the rope goes up.
    if (c.kind === 0 && c.land >= 0 && c.land !== node) out.push({ crack: i, to: c.land });
    if (c.kind === 0 && c.land === node && c.top >= 0) out.push({ crack: i, to: c.top });
    if (c.kind === 2) out.push({ crack: i, to: n + i });
  });
  return out;
};

/** The ways from the top ledge down to the goal (as their cracks), each node once: how many, and the first few. */
export const cliffRoutes = (L: CliffLayout, cap = 6): number[][] => {
  const goal = L.ledges.findIndex((l) => l.goal);
  const out: number[][] = [];
  const seen = new Set<number>();
  const walk = (node: number, way: number[]): void => {
    if (out.length >= cap) return;
    if (node === goal) {
      out.push([...way]);
      return;
    }
    seen.add(node);
    for (const e of cliffEdges(L, node)) if (!seen.has(e.to)) walk(e.to, [...way, e.crack]);
    seen.delete(node);
  };
  walk(0, []);
  return out;
};

/** Which nodes reach the goal (down or by any rope). */
const reachesGoal = (L: CliffLayout): Set<number> => {
  const goal = L.ledges.findIndex((l) => l.goal);
  const total = L.ledges.length + L.cracks.length;
  const ok = new Set<number>([goal]);
  for (let pass = 0; pass < total; pass++) for (let v = 0; v < total; v++) if (!ok.has(v) && (v < L.ledges.length || (L.cracks[v - L.ledges.length] as CliffCrackLayout).kind === 2) && cliffEdges(L, v).some((e) => ok.has(e.to))) ok.add(v);
  return ok;
};

/**
 * The obvious way down: from where the hero lands, the nearest firm crack under it with a ledge below, each time,
 * never a crumbling one. The ledge it stops on (−1: it went round in a loop).
 */
export const cliffObvious = (L: CliffLayout): number => {
  let node = 0;
  let at = hexToWorld(L.start.q, L.start.r);
  for (let step = 0; step < 8; step++) {
    const lv = (L.ledges[node] as CliffLedgeLayout).level;
    let best = -1;
    let bd = 0x7fffffff;
    for (const e of cliffEdges(L, node)) {
      const c = L.cracks[e.crack] as CliffCrackLayout;
      if (c.kind !== 0 || (L.ledges[e.to] as CliffLedgeLayout).level <= lv) continue;
      const d = d2(at, crackPoint(c));
      if (d < bd) {
        bd = d;
        best = e.crack;
      }
    }
    if (best < 0) return node;
    const c = L.cracks[best] as CliffCrackLayout;
    node = c.land;
    at = crackPoint(c);
    at = { x: at.x, y: at.y + 2600 };
  }
  return -1;
};

const segment = (rng: RngState, level: number, x0: number, x1: number, goal = false): { level: number; x0: number; x1: number; goal: boolean } => ({ level, x0: x0 + rngRange(rng, -300, 300), x1: x1 + rngRange(rng, -300, 300), goal });

/**
 * The wall from the seed (`night`: the test's). Up to 4000 tries, each checked on paper; the last try's wall if none
 * passes (never seen in the tests: the first few hundred seeds pass within a few dozen).
 */
export const wallLayout = (seed: number, night: boolean): CliffLayout => {
  const R = WALL_RADIUS;
  const rng = seedRng(deriveSeed(seed, night ? 0x9a10 : 0x3a11));
  let last: CliffLayout | null = null;
  for (let attempt = 0; attempt < 4000; attempt++) {
    const flip = rngInt(rng, 2) ? 1 : -1;
    // The segments, west to east before the flip: the top one wide; two at each level under; the goal at the foot.
    const gap1 = rngRange(rng, 1200, 2600);
    // At night the third level is all west and the foot's ledge east: under the east half of the second face there is
    // only the void, and a crumbling crack there is the one way across.
    const segs = (
      night
        ? [
            segment(rng, 0, -7000, 7000),
            segment(rng, 1, -7600, -gap1),
            segment(rng, 1, gap1, 7600),
            segment(rng, 2, -7600, rngRange(rng, -5200, -4600)),
            segment(rng, 2, rngRange(rng, -3800, -3200), rngRange(rng, -1400, -600)),
            segment(rng, 3, -7600, -4200),
            segment(rng, 3, rngRange(rng, 3800, 4600), 7600, true),
          ]
        : [
            segment(rng, 0, -7000, 7000),
            segment(rng, 1, -7600, -gap1),
            segment(rng, 1, gap1, 7600),
            segment(rng, 2, -7600, rngRange(rng, -3800, -1800)),
            segment(rng, 2, rngRange(rng, 400, 2400), 7600),
            segment(rng, 3, -7600, -3400),
            segment(rng, 3, 2600, 7600, true),
          ]
    ).map((g) => (flip > 0 ? g : { ...g, x0: -g.x1, x1: -g.x0 }));
    const ledges: CliffLedgeLayout[] = segs.map((g) => ({ cells: ledgeCells(R, LEVEL_ROWS[g.level] as number, g.x0, g.x1), level: g.level, x0: g.x0, x1: g.x1, goal: g.goal }));
    if (ledges.some((l) => l.cells.length < 4)) continue;
    // The cracks: four or five a face, on its middle rows, apart from each other.
    const cracks: CliffCrackLayout[] = [];
    for (let f = 0; f < 3; f++) {
      const top = (LEVEL_ROWS[f] as number) + 2;
      const n = rngRange(rng, 4, 5);
      for (let k = 0; k < 40 && cracks.filter((c) => c.h.r >= top && c.h.r <= top + 2).length < n; k++) {
        const h = atRow(top + rngRange(rng, 0, 2), rngRange(rng, -7200, 7200));
        if (hexLen(h.q, h.r) > R - 1 || cracks.some((c) => Math.abs(wx(c.h) - wx(h)) < 1500 && Math.abs(c.h.r - h.r) < 3)) continue;
        const x = wx(h);
        const land = ledges.findIndex((l) => l.level === f + 1 && x >= l.x0 - 400 && x <= l.x1 + 400);
        const over = ledges.findIndex((l) => l.level === f && x >= l.x0 - 400 && x <= l.x1 + 400);
        const roll = rngInt(rng, 100);
        const kind = land < 0 ? (roll < 62 ? 2 : 1) : roll < 55 ? 0 : 1;
        cracks.push({ h, kind, land, top: over });
      }
    }
    const L: CliffLayout = { rocks: [], ledges, cracks, nests: [], holes: [], stones: [], start: atRow(-9, flip * -4000), anchor: { q: 0, r: 0 }, crew: -flip };
    last = L;
    const routes = cliffRoutes(L);
    if (routes.length < 1 || routes.length > 2) continue;
    const goal = ledges.findIndex((l) => l.goal);
    // Nothing on the wall strands the hero: from every ledge the goal is reached, by its ropes back up if it must.
    const ok = reachesGoal(L);
    if (!ledges.every((l, i) => l.goal || ok.has(i))) continue;
    const top = cliffEdges(L, 0);
    if (!night) {
      // The wall teaches: a hollow crack in reach of the top; a way through a crumbling crack.
      const hollowNear = cracks.some((c) => c.kind === 1 && (L.ledges[0] as CliffLedgeLayout).cells.some((h) => inReach(hexToWorld(h.q, h.r), crackPoint(c))));
      if (!hollowNear || !routes.some((w) => w.some((i) => (cracks[i] as CliffCrackLayout).kind === 2))) continue;
      if (top.length < 2) continue;
    } else {
      // The test: no ledge reaches the goal's cracks; the obvious way down ends on a ledge with no way on.
      if (ledges.some((l, i) => i !== goal && cliffEdges(L, i).some((e) => e.to === goal))) continue;
      const o = cliffObvious(L);
      if (o < 0 || o === goal || ledges[o]?.level === 0) continue;
      if (!routes.every((w) => w.some((i) => (cracks[i] as CliffCrackLayout).kind === 2))) continue;
    }
    // Nests by the faces (crows), deep cracks (the shriekers'), a hollow stone a ledge (the night's).
    const used = new Set(cracks.map((c) => key(c.h)));
    const nests: Hex[] = [];
    const holes: Hex[] = [];
    for (let f = 0; f < 3; f++) {
      const top0 = (LEVEL_ROWS[f] as number) + 2;
      // (At night no crows: the shriekers and their mother. The crows sleep.)
      for (let k = 0; k < 20 && !night && nests.filter((h) => h.r >= top0 && h.r <= top0 + 2).length < 2; k++) {
        const h = atRow(top0 + rngRange(rng, 0, 2), rngRange(rng, -7000, 7000));
        if (hexLen(h.q, h.r) > R - 1 || used.has(key(h))) continue;
        used.add(key(h));
        nests.push(h);
      }
      if (night)
        for (let k = 0; k < 20 && holes.filter((h) => h.r >= top0 && h.r <= top0 + 2).length < 2; k++) {
          const h = atRow(top0 + rngRange(rng, 0, 2), rngRange(rng, -7000, 7000));
          if (hexLen(h.q, h.r) > R - 1 || used.has(key(h))) continue;
          used.add(key(h));
          holes.push(h);
        }
    }
    const stones = night
      ? ledges.flatMap((l, i) => {
          if (l.goal) return [];
          const back = l.cells.filter((h) => h.r === (LEVEL_ROWS[l.level] as number));
          const h = back[rngInt(rng, back.length)] as Hex;
          return [{ h, ledge: i }];
        })
      : [];
    const open = ledges.flatMap((l) => l.cells);
    return { ...L, rocks: rocksOf(R, open), nests, holes, stones };
  }
  const L = last as CliffLayout;
  return { ...L, rocks: rocksOf(R, L.ledges.flatMap((l) => l.cells)) };
};

/** A point's distance to a crack (for the drawing's reach arcs and the bot). */
export const cliffDist = (a: { x: number; y: number }, b: { x: number; y: number }): number => isqrt(d2(a, b));
